import {
  useAccountUsers,
  useAddress,
  useCreateAddress,
  useCreateParty,
  useOrganisation,
  useParty,
  useSetPartyDocumentLayouts,
  useSetPartyRequiredOrderReferences,
  useUpdateAddress,
  useUpdateParty,
} from '@my/directory-api/hooks';
import type {
  Address,
  BillingFrequencyValue,
  CreateAddressRequest,
  CreatePartyRequest,
  InvitationStatusValue,
  Party,
  PartyStatusValue,
  PaymentMethodValue,
  PaymentTermBasisValue,
  SupplierTypeValue,
  UpdateAddressRequest,
  UpdatePartyRequest,
} from '@my/directory-api';
import { useServiceTags } from '@my/tms-api/hooks';
import type { ServiceTag } from '@my/tms-api';
import {
  AddressInlineEditor,
  ConfirmDialog,
  ErrorBanner,
  FormSection,
  Loading,
  PageHeader,
  SelectInput,
  Switch,
  TextInput,
  showToast,
  type ExtraField,
  type SelectInputOption,
} from '@my/ui';
// Cross-feature import — ExtraFieldsList lives under settings/organisation
// today because that was its first user. Both Organisation and Party now
// store custom fields as a JSON-serialised `ExtraField[]` blob, so the
// component is naturally shared. TODO: lift to a neutral location
// (`features/_shared/` or `@my/ui`) once a third consumer appears, so the
// settings → parties direction of dependency goes away.
import { ExtraFieldsList } from '@/features/settings/organisation/ExtraFieldsList';
import { useDocumentTemplates } from '@/features/settings/documents/lib/useDocumentTemplates';
import type { DocumentTemplate } from '@/features/settings/documents/lib/types';
import { readLayoutPref, writeLayoutPrefs } from '@/utils/documentLayoutPrefs';
import {
  parseOrderReferenceLabels,
  parseRequiredOrderReferenceSlots,
  resolveOrderReferenceLabel,
  serialiseRequiredOrderReferenceSlots,
} from '@/utils/orderReferenceLabels';
import { COUNTRIES, CURRENCIES, findCountry } from '@my/platform/options';
import { useAuth0 } from '@auth0/auth0-react';
import { ArrowLeft, Check, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import {
  describePaymentTerms,
  PAYMENT_TERM_BASIS_LABEL,
  resolvePaymentTerms,
} from '@/utils/paymentTerms';
import { PartyAddressesSection } from './PartyAddressesSection';
import { PartyContactsSection } from './PartyContactsSection';
import { PartyDocumentsSection } from './PartyDocumentsSection';
import { PartyRatesAndChargesSection } from './PartyRatesAndChargesSection';
import { PartyHistoryButton } from './PartyHistoryButton';
import type { PartyListMode } from './PartyListPage';
import { useDeletePartyViaTms } from './useDeletePartyViaTms';

interface PartyDetailsPageProps {
  mode: PartyListMode;
}

const STATUS_OPTIONS: PartyStatusValue[] = ['Active', 'Suspended', 'CeasedTrading'];
const BILLING_FREQUENCY_OPTIONS: BillingFrequencyValue[] = [
  'Weekly',
  'Monthly',
  'Quarterly',
  'Yearly',
];
const PAYMENT_METHOD_OPTIONS: PaymentMethodValue[] = ['Cash', 'Credit', 'SelfBill', 'Internal'];

/**
 * Order of values displayed in the supplier-subtype picker. Mirrors the
 * BE enum exactly so the picker reads as "pick one of these eight" —
 * even though most operators will choose Generic / ShippingCompany /
 * Haulier, the rest stay visible so subtypes don't surface as a
 * mysterious "and a few others somewhere".
 */
const SUPPLIER_TYPE_OPTIONS: SupplierTypeValue[] = [
  'Generic',
  'ShippingCompany',
  'ShippingAgency',
  'Haulier',
  'Fuel',
  'Garage',
  'Warehouse',
  'Other',
];

// Country + Currency option formats deliberately mirror the
// OrganisationPage so dropdowns read identically across the app — the
// flag-emoji prefix on countries and the `Name (ISO)` shape on
// currencies. Keeping these in lock-step avoids the cognitive whiplash
// of two different label conventions for the same underlying values.
const COUNTRY_OPTIONS: SelectInputOption[] = COUNTRIES.map((c) => ({
  value: c.isoCode,
  label: `${c.flagEmoji} ${c.name}`,
}));

const CURRENCY_OPTIONS: SelectInputOption[] = CURRENCIES.map((c) => ({
  value: c.isoCode,
  label: `${c.name} (${c.isoCode})`,
}));

/**
 * Compute a partial `PartyDraft` patch when the **primary address country**
 * changes. Same conservative cascade rule as the OrganisationPage uses for
 * its regional fields — see `@my/platform/options/applyCountryDefaults` —
 * just scoped to the two party-side fields we want to keep in step:
 *
 *  - `currencyIsoCode` → the new country's `defaultCurrencyIsoCode`.
 *  - `taxSchemeCountryCode` → the new country's ISO code itself (tax scheme
 *    typically tracks the operating country).
 *
 * Per-field rule:
 *  - Empty current value → patch to the new country's default.
 *  - Current value still equals the previous country's default → patch
 *    (the value looks "auto-applied", so refresh it).
 *  - Otherwise → leave alone (operator picked an explicit override; never
 *    overwrite that).
 *
 * Returns a partial object; spread into `setPartyDraft`. Empty object means
 * nothing to patch.
 */
function applyPartyCountryDefaults(
  newCountryIso: string,
  previousCountryIso: string | null,
  current: { currencyIsoCode: string; taxSchemeCountryCode: string },
): Partial<PartyDraft> {
  const nextCountry = findCountry(newCountryIso);
  if (!nextCountry) return {};
  const prevCountry = previousCountryIso ? findCountry(previousCountryIso) : null;
  const patch: Partial<PartyDraft> = {};

  // Currency — patch if empty OR still equal to the previous country's
  // default currency (i.e. looks auto-applied from before the country
  // change).
  const nextCurrency = nextCountry.defaultCurrencyIsoCode;
  const prevCurrency = prevCountry?.defaultCurrencyIsoCode;
  if (!current.currencyIsoCode || current.currencyIsoCode === prevCurrency) {
    if (current.currencyIsoCode !== nextCurrency) patch.currencyIsoCode = nextCurrency;
  }

  // Tax scheme country — patch if empty OR still equal to the previous
  // primary-address country (i.e. the operator hasn't differentiated tax
  // jurisdiction from the address yet).
  if (!current.taxSchemeCountryCode || current.taxSchemeCountryCode === previousCountryIso) {
    if (current.taxSchemeCountryCode !== newCountryIso) {
      patch.taxSchemeCountryCode = newCountryIso;
    }
  }

  return patch;
}

/**
 * Parse a server-side `extraFields` text payload back into an array of
 * `ExtraField`. Tolerant of:
 *   - `null` / missing → `[]`
 *   - JSON-stringified array (current expected shape)
 *   - `{ extraFields: "..." }` legacy wrapper (defensive — shouldn't surface
 *     from the new BE but cheap to handle).
 *   - malformed JSON → `[]` (drop silently rather than blow up the page).
 *
 * Mirrors the helper in `settings/organisation/lib/formMappers.ts` —
 * once a third consumer appears, lift to a shared module.
 */
function parseExtraFields(raw: unknown): ExtraField[] {
  if (raw == null) return [];
  let text: string;
  if (typeof raw === 'object') {
    const nested = (raw as { extraFields?: unknown }).extraFields;
    text = typeof nested === 'string' ? nested : '';
  } else if (typeof raw === 'string') {
    text = raw;
  } else {
    return [];
  }
  if (!text.trim()) return [];
  try {
    const parsed = JSON.parse(text) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (x): x is ExtraField =>
        typeof x === 'object' &&
        x !== null &&
        typeof (x as ExtraField).fieldLabel === 'string' &&
        typeof (x as ExtraField).fieldValue === 'string' &&
        ['text', 'number', 'date', 'boolean'].includes((x as ExtraField).fieldType as string),
    );
  } catch {
    return [];
  }
}

/**
 * Wave 3 details page — handles BOTH edit and create modes.
 *
 * Edit (`/customers/:id`, `/suppliers/:id`):
 *   - Loads the party + its primary + billing addresses (3 parallel reads).
 *   - Renders the basic-info form, the service-tag chip row, and inline
 *     editors for both addresses.
 *   - Save fires updateAddress x2 + updateParty in parallel.
 *
 * Create (`/customers/new`, `/suppliers/new`):
 *   - Empty form. The party type is locked to the URL mode.
 *   - "Same address for billing" toggle ON by default — most parties bill
 *     to their primary site.
 *   - Save sequences createAddress(primary) → createAddress(billing) →
 *     createParty(...). Address rows are initially partyId=null because the
 *     party doesn't exist yet; the BE finds them via Party.PrimaryAddressId
 *     / BillingAddressId regardless. Linking the reverse pointer can be a
 *     follow-up if `getByPartyAsync` ever surfaces them in a UI.
 */
export function PartyDetailsPage({ mode }: PartyDetailsPageProps) {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const routeBase = mode === 'Customer' ? '/customers' : '/suppliers';

  return (
    <PartyDetailsEditor partyId={id ?? null} mode={mode} routeBase={routeBase} isNew={isNew} />
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Editor — handles both create and edit. The mode is communicated through
// `isNew` (rather than letting the editor infer it from `partyId`) so the
// switch stays explicit at the call site.
// ────────────────────────────────────────────────────────────────────────────

interface PartyDetailsEditorProps {
  partyId: string | null;
  mode: PartyListMode;
  routeBase: string;
  isNew: boolean;
}

interface PartyDraft {
  code: string;
  name: string;
  /**
   * Supplier sub-classification. Defaults to `Generic` for new
   * Supplier parties (no blank option in the picker — every supplier
   * has SOME classification, the safe fallback is Generic). On the
   * wire we still send `null` for Customer parties since subtype is
   * supplier-only at the BE level.
   */
  subtype: SupplierTypeValue;
  status: PartyStatusValue;
  taxNumber: string;
  taxSchemeCountryCode: string;
  currencyIsoCode: string;
  billingName: string;
  billingFrequency: BillingFrequencyValue;
  paymentMethod: PaymentMethodValue;
  creditLimit: string; // string for the input; parsed to number on save
  /** '' = inherit the organisation's default terms. */
  paymentTermDays: string;
  paymentTermBasis: PaymentTermBasisValue;
  /** '' = print with the organisation's default invoice layout. */
  preferredInvoiceTemplateId: string;
  /** '' = print with the organisation's default credit-note layout. */
  preferredCreditNoteTemplateId: string;
  /**
   * The stored `{ app: { kind: layoutKey } }` map, carried so saving can merge
   * the two fields above into it. Other apps' branches live in the same column
   * and must survive a save from here.
   */
  storedLayoutPrefsJson: string | null;
  euVatStatement: boolean;
  /** Network/invite ID — surfaced in column 1 for parity with the legacy
   *  layout. The full network/invite subsystem is deferred; the field is
   *  editable as a free-form string for now. */
  networkId: string;
  /**
   * Logical FK to a User in the same account. Surfaced as a dropdown of
   * users in column 3 — empty string means "no owner assigned" and
   * serialises to `null` on save.
   */
  accountOwnerId: string;
  accountSystemName: string;
  accountSystemCode: string;
  /**
   * Operator-defined custom fields. Stored on the BE as a JSON-stringified
   * `ExtraField[]` in the `extraFields` text column; parsed on read,
   * serialised on write. Empty array sends `null` rather than `"[]"`.
   */
  extraFields: ExtraField[];
  /** Comma-joined ServiceTag names — same wire shape as the BE column. */
  tags: string;
  /**
   * Order reference slots this customer insists on, 1-based and ascending.
   *
   * Written through its own endpoint rather than the party PUT (see
   * `useSetPartyRequiredOrderReferences`), so it is held here only to drive the
   * chips and to work out whether anything changed on save.
   */
  requiredOrderReferenceSlots: number[];
}

interface AddressDraft {
  /** Short reference code, e.g. "HQ", "WAREHOUSE-01". Optional. */
  code: string;
  name: string;
  /** Google-formatted address line — full single string. */
  fullAddress: string;
  city: string;
  postcode: string;
  countryIsoCode: string;
  /** Populated by Places autocomplete; null until verified. Persisted on save. */
  latitude: number | null;
  longitude: number | null;
  /** Google Place ID — required on save for new rows. */
  placeId: string;
  /**
   * Where the coordinates came from: `Google` (picked / Find on map) or
   * `Manual` (pin dragged or tapped). Forwarded on save so Directory never
   * refreshes a hand-placed pin back off the gate.
   */
  coordinatesSource: 'Google' | 'Manual' | null;
}

const EMPTY_PARTY_DRAFT: PartyDraft = {
  code: '',
  name: '',
  subtype: 'Generic',
  status: 'Active',
  taxNumber: '',
  taxSchemeCountryCode: 'IE',
  currencyIsoCode: 'EUR',
  billingName: '',
  billingFrequency: 'Monthly',
  paymentMethod: 'Cash',
  creditLimit: '0',
  paymentTermDays: '',
  paymentTermBasis: 'InvoiceDate',
  preferredInvoiceTemplateId: '',
  preferredCreditNoteTemplateId: '',
  storedLayoutPrefsJson: null,
  euVatStatement: false,
  networkId: '',
  accountOwnerId: '',
  accountSystemName: '',
  accountSystemCode: '',
  extraFields: [],
  tags: '',
  requiredOrderReferenceSlots: [],
};

const EMPTY_ADDRESS_DRAFT: AddressDraft = {
  code: '',
  name: '',
  fullAddress: '',
  city: '',
  postcode: '',
  countryIsoCode: 'IE',
  latitude: null,
  longitude: null,
  placeId: '',
  coordinatesSource: null,
};

function partyToDraft(p: Party): PartyDraft {
  return {
    code: p.code,
    name: p.name,
    // BE may store NULL for legacy Supplier parties created before
    // Subtype became a typed enum; load those as Generic so the
    // picker always has a real value and saving doesn't surface a
    // surprise null somewhere downstream.
    subtype: p.subtype ?? 'Generic',
    status: p.status,
    taxNumber: p.taxNumber ?? '',
    taxSchemeCountryCode: p.taxSchemeCountryCode,
    currencyIsoCode: p.currencyIsoCode,
    billingName: p.billingName,
    billingFrequency: p.billingFrequency,
    paymentMethod: p.paymentMethod,
    creditLimit: p.creditLimit.toString(),
    paymentTermDays: p.paymentTermDays === null ? '' : String(p.paymentTermDays),
    paymentTermBasis: p.paymentTermBasis ?? 'InvoiceDate',
    preferredInvoiceTemplateId: readLayoutPref(p.preferredDocumentLayoutsJson, 'Invoice') ?? '',
    preferredCreditNoteTemplateId:
      readLayoutPref(p.preferredDocumentLayoutsJson, 'CreditNote') ?? '',
    storedLayoutPrefsJson: p.preferredDocumentLayoutsJson,
    euVatStatement: p.euVatStatement,
    networkId: p.networkId ?? '',
    accountOwnerId: p.accountOwnerId ?? '',
    accountSystemName: p.accountSystemName ?? '',
    accountSystemCode: p.accountSystemCode ?? '',
    extraFields: parseExtraFields(p.extraFields),
    tags: p.tags ?? '',
    requiredOrderReferenceSlots: parseRequiredOrderReferenceSlots(p.tmsRequiredOrderReferencesJson),
  };
}

function addressToDraft(a: Address): AddressDraft {
  return {
    code: a.code ?? '',
    name: a.name,
    fullAddress: a.fullAddress,
    city: a.city ?? '',
    postcode: a.postcode ?? '',
    countryIsoCode: a.countryIsoCode,
    latitude: a.latitude ?? null,
    longitude: a.longitude ?? null,
    placeId: a.placeId,
    coordinatesSource: a.coordinatesSource ?? null,
  };
}

function PartyDetailsEditor({ partyId, mode, routeBase, isNew }: PartyDetailsEditorProps) {
  const { t } = useTranslation(['parties', 'common']);
  const navigate = useNavigate();
  const { user } = useAuth0();
  // In create mode there's no party loaded yet, so we read the org id from
  // the access token claim (same pattern ResourcesPage uses). In edit mode
  // we still prefer the loaded party's organisationId — guards against
  // mid-session org switches by trusting the data we've already fetched.
  const tokenOrgId = useMemo(() => getCurrentOrganisationId(user), [user]);

  const isCustomer = mode === 'Customer';
  const partyQuery = useParty(partyId);
  const partyOrgId = partyQuery.data?.organisationId ?? null;
  const orgId = partyOrgId ?? tokenOrgId;

  // Load the linked addresses once the party arrives. Both queries are
  // gated on the party id; in create mode the args are null and the
  // queries stay disabled.
  const primaryAddressQuery = useAddress(partyQuery.data?.primaryAddressId ?? null);
  const billingAddressQuery = useAddress(partyQuery.data?.billingAddressId ?? null);

  // Org + main-office address — only used in create mode to seed sensible
  // defaults (currency, tax country, address country) from the parent
  // organisation rather than the hard-coded EUR/IE fallbacks. Edit mode
  // ignores these (the loaded party already has its own values).
  // Loaded in BOTH modes: create mode seeds defaults from it (below, still
  // gated on `isNew`), and edit mode needs the house payment terms to show what
  // a customer inherits when it has none of its own.
  const orgQuery = useOrganisation(orgId);
  const officeAddressQuery = useAddress(
    isNew ? (orgQuery.data?.mainOfficeAddressId ?? null) : null,
  );

  const serviceTagsQuery = useServiceTags(orgId);
  // Account users — populates the Account Owner dropdown. Account-scoped
  // (not org-scoped) which is intentional: any user in the same account
  // can be assigned as a party's owner, regardless of which org they
  // primarily operate against.
  const accountUsersQuery = useAccountUsers();
  const updatePartyMutation = useUpdateParty();
  const createPartyMutation = useCreateParty();
  const updateAddressMutation = useUpdateAddress();
  const createAddressMutation = useCreateAddress();
  // Through TMS, which refuses while the party is on orders.
  const deletePartyMutation = useDeletePartyViaTms();
  const setRequiredReferencesMutation = useSetPartyRequiredOrderReferences();
  const setDocumentLayoutsMutation = useSetPartyDocumentLayouts();

  const isBusy =
    setDocumentLayoutsMutation.isPending ||
    setRequiredReferencesMutation.isPending ||
    updatePartyMutation.isPending ||
    createPartyMutation.isPending ||
    updateAddressMutation.isPending ||
    createAddressMutation.isPending ||
    deletePartyMutation.isPending;

  // Delete confirmation — same ConfirmDialog pattern as the list page.
  const [pendingDelete, setPendingDelete] = useState(false);

  // Service-tag library for the chip row, filtered by mode (Customers tags
  // on /customers, Suppliers tags on /suppliers, plus catch-all `null` apply-
  // to-all tags on both). Status=Active only.
  const applicableTags: ServiceTag[] = useMemo(() => {
    const targetArea = isCustomer ? 'Customers' : 'Suppliers';
    return (serviceTagsQuery.data ?? [])
      .filter((tag) => {
        if (tag.status !== 'Active') return false;
        const apply = tag.applyToServiceTypes?.trim();
        if (!apply) return true;
        return apply
          .split(',')
          .map((s) => s.trim())
          .includes(targetArea);
      })
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [serviceTagsQuery.data, isCustomer]);

  // Account-owner dropdown options — sorted by display name. The leading
  // empty option doubles as the "no owner" choice; serialised to `null`
  // on save by the create / update body builders above.
  const accountOwnerOptions: SelectInputOption[] = useMemo(() => {
    const users = accountUsersQuery.data ?? [];
    const opts = users
      .map((u) => ({
        value: u.id,
        label: u.fullName || u.email,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    return [{ value: '', label: '—' }, ...opts];
  }, [accountUsersQuery.data]);

  // ── Drafts ────────────────────────────────────────────────────────────
  const [partyDraft, setPartyDraft] = useState<PartyDraft>(EMPTY_PARTY_DRAFT);
  const [primaryAddressDraft, setPrimaryAddressDraft] = useState<AddressDraft>(EMPTY_ADDRESS_DRAFT);
  const [billingAddressDraft, setBillingAddressDraft] = useState<AddressDraft>(EMPTY_ADDRESS_DRAFT);
  // Default ON in create mode (most parties bill to their primary site);
  // OFF in edit mode (assume independent until operator opts in).
  const [billingSameAsPrimary, setBillingSameAsPrimary] = useState<boolean>(isNew);
  // Track whether the operator has manually edited the address-name fields
  // in create mode. Until they do, the address name mirrors the party name
  // as it's typed (most-common shape: party "Acme Corp" → address named
  // "Acme Corp"). Once they override the address name, mirroring stops so
  // we don't clobber their override on subsequent party-name keystrokes.
  // Edit mode never auto-syncs — these flags are inert there.
  const [primaryAddressNameDirty, setPrimaryAddressNameDirty] = useState(false);
  const [billingAddressNameDirty, setBillingAddressNameDirty] = useState(false);

  // Seed drafts once the server data lands. Re-seed only on first arrival
  // so an external mutation invalidating the queries doesn't blow away
  // unsaved local edits.
  const [seeded, setSeeded] = useState(isNew);
  useEffect(() => {
    if (seeded) return;
    if (partyQuery.data && primaryAddressQuery.data && billingAddressQuery.data) {
      setPartyDraft(partyToDraft(partyQuery.data));
      setPrimaryAddressDraft(addressToDraft(primaryAddressQuery.data));
      setBillingAddressDraft(addressToDraft(billingAddressQuery.data));
      // Detect "billing same as primary" from the server state — when the
      // party's two address pointers reference the *same* Address row, the
      // toggle should reflect that and hide the (redundant) billing editor.
      // The reverse direction (split records with identical content) is
      // intentionally NOT detected: a separate billing record is the
      // operator's signal that they want to keep the records independent
      // even when contents currently match.
      if (partyQuery.data.primaryAddressId === partyQuery.data.billingAddressId) {
        setBillingSameAsPrimary(true);
      }
      setSeeded(true);
    }
  }, [partyQuery.data, primaryAddressQuery.data, billingAddressQuery.data, seeded]);

  // New-mode seeding — once the parent org loads, copy its currency, tax
  // country, and main-office address country into the empty drafts so
  // the form starts pre-filled with sensible defaults instead of the
  // hard-coded EUR / IE fallbacks. Runs exactly once per page mount in
  // create mode (gated on `orgDefaultsSeeded`); subsequent operator edits
  // are not overwritten.
  //
  // The wait for `officeAddressQuery.data` is conditional: if the org has
  // a `mainOfficeAddressId` we hold for the address fetch (so address
  // country can lift from the actual office record). If there's no office
  // address, we proceed immediately and fall back to `org.countryIsoCode`.
  const [orgDefaultsSeeded, setOrgDefaultsSeeded] = useState(false);
  useEffect(() => {
    if (!isNew || orgDefaultsSeeded) return;
    const org = orgQuery.data;
    if (!org) return;
    if (org.mainOfficeAddressId && !officeAddressQuery.data) return;
    const officeCountry = officeAddressQuery.data?.countryIsoCode ?? org.countryIsoCode;
    // Address name is no longer seeded here — it mirrors the party name
    // as the operator types (see the party-name onChange below). Country
    // and currency still cascade from the parent org / office address so
    // the form starts geographically correct without operator effort.
    setPartyDraft((prev) => ({
      ...prev,
      currencyIsoCode: org.currencyIsoCode,
      taxSchemeCountryCode: org.countryIsoCode,
    }));
    setPrimaryAddressDraft((prev) => ({
      ...prev,
      countryIsoCode: officeCountry,
    }));
    setBillingAddressDraft((prev) => ({
      ...prev,
      countryIsoCode: officeCountry,
    }));
    setOrgDefaultsSeeded(true);
  }, [isNew, orgQuery.data, officeAddressQuery.data, orgDefaultsSeeded]);

  // Payment terms only mean something for a billing treatment that has a
  // payment period: credit accounts are invoiced on them, and self-billing
  // accounts still pay on a cycle. Prepay settles on despatch and internal
  // accounts are never invoiced.
  const showsPaymentTerms =
    partyDraft.paymentMethod === 'Credit' || partyDraft.paymentMethod === 'SelfBill';

  // Whether this party is invoiced, which is what a layout preference is for.
  // `mode` is only which side of the app the operator came in from — the
  // party's own type decides, and a `Both` party is invoiced like any customer.
  const partyType = partyQuery.data?.type ?? mode;
  const billsAsCustomer = partyType === 'Customer' || partyType === 'Both';

  // The organisation's reference labels, so the chips read the way the operator
  // named the fields. Only the slots the organisation actually configured are
  // offered: a customer cannot demand a reference the order screen never shows.
  const orderReferenceLabels = useMemo(
    () => parseOrderReferenceLabels(orgQuery.data?.tmsOrderReferenceLabelsJson),
    [orgQuery.data?.tmsOrderReferenceLabelsJson],
  );
  // Every configured slot is offered, unnamed ones included: an unnamed slot
  // still renders on the order screen as "Reference 3", so it is a real field an
  // operator can fill and a customer can demand.
  const configuredReferenceSlots = useMemo(
    () => orderReferenceLabels.map((_label, index) => index + 1),
    [orderReferenceLabels],
  );

  // Invoice layouts to choose from, and which one an unset preference means.
  // Read through the same hook the builder and the invoicing tabs use, so the
  // list here cannot disagree with what actually prints.
  const { templates: documentTemplates } = useDocumentTemplates(orgId ?? null);
  // Every layout of each kind is selectable, the shipped baseline included: the
  // stored value is a layout KEY, so "stock-invoice-standard" pins just as well
  // as a saved layout's id.
  const invoiceLayouts = useMemo(
    () => documentTemplates.filter((tpl) => tpl.docType === 'Invoice'),
    [documentTemplates],
  );
  const creditNoteLayouts = useMemo(
    () => documentTemplates.filter((tpl) => tpl.docType === 'CreditNote'),
    [documentTemplates],
  );
  const layoutsLoaded = documentTemplates.length > 0;

  // The house terms this customer falls back to when it has none of its own.
  const inheritedTerms = useMemo(() => resolvePaymentTerms(null, orgQuery.data), [orgQuery.data]);

  // Pre-resolve label maps (typed-keys i18n won't accept template literals).
  const statusLabels: Record<PartyStatusValue, string> = {
    Active: t('parties:status.active'),
    Suspended: t('parties:status.suspended'),
    CeasedTrading: t('parties:status.ceasedTrading'),
  };
  const subtypeLabels: Record<SupplierTypeValue, string> = {
    Generic: t('parties:subtype.generic'),
    ShippingCompany: t('parties:subtype.shippingCompany'),
    ShippingAgency: t('parties:subtype.shippingAgency'),
    Haulier: t('parties:subtype.haulier'),
    Fuel: t('parties:subtype.fuel'),
    Garage: t('parties:subtype.garage'),
    Warehouse: t('parties:subtype.warehouse'),
    Other: t('parties:subtype.other'),
  };
  // No blank entry — every supplier carries a Subtype. Operators who
  // don't know yet pick `Generic` (the default), and can revisit
  // later. This keeps the BE column from carrying a mix of "null"
  // and "Generic" rows that mean the same thing.
  const subtypeOptions: SelectInputOption[] = SUPPLIER_TYPE_OPTIONS.map((v) => ({
    value: v,
    label: subtypeLabels[v],
  }));
  const billingFrequencyLabels: Record<BillingFrequencyValue, string> = {
    Weekly: t('parties:billingFrequency.weekly'),
    Monthly: t('parties:billingFrequency.monthly'),
    Quarterly: t('parties:billingFrequency.quarterly'),
    Yearly: t('parties:billingFrequency.yearly'),
  };
  const paymentMethodLabels: Record<PaymentMethodValue, string> = {
    Cash: t('parties:paymentMethod.cash'),
    Credit: t('parties:paymentMethod.credit'),
    SelfBill: t('parties:paymentMethod.selfBill'),
    Internal: t('parties:paymentMethod.internal'),
  };
  // Read-only display labels for the InvitationStatus enum — driven by
  // the (deferred) network / invite subsystem; for now most parties stay
  // 'Not Invited'.
  const invitationStatusLabels: Record<InvitationStatusValue, string> = {
    NotInvited: t('parties:invitationStatus.notInvited'),
    Pending: t('parties:invitationStatus.pending'),
    Accepted: t('parties:invitationStatus.accepted'),
    Expired: t('parties:invitationStatus.expired'),
    Revoked: t('parties:invitationStatus.revoked'),
    Rejected: t('parties:invitationStatus.rejected'),
  };

  // ── Tag toggle ─────────────────────────────────────────────────────────
  const tagSet = useMemo(() => {
    return new Set(
      partyDraft.tags
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .map((s) => s.toLowerCase()),
    );
  }, [partyDraft.tags]);

  const toggleTag = (tag: ServiceTag) => {
    const lower = tag.name.toLowerCase();
    const list = partyDraft.tags
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const idx = list.findIndex((s) => s.toLowerCase() === lower);
    if (idx >= 0) {
      list.splice(idx, 1);
    } else {
      list.push(tag.name);
    }
    setPartyDraft({ ...partyDraft, tags: list.join(',') });
  };

  // ── Primary-address change funnel ────────────────────────────────────
  // All updates to the primary address go through this so a country
  // change can cascade defaults onto the party-level fields (currency,
  // tax country). Same conservative cascade rule as the OrganisationPage:
  // empty / equal-to-previous-default fields get patched; explicit
  // operator overrides are preserved. See `applyPartyCountryDefaults`.
  const handlePrimaryAddressChange = (next: AddressDraft) => {
    const previousCountry = primaryAddressDraft.countryIsoCode;
    const newCountry = next.countryIsoCode;
    if (newCountry && newCountry !== previousCountry) {
      const patch = applyPartyCountryDefaults(newCountry, previousCountry || null, {
        currencyIsoCode: partyDraft.currencyIsoCode,
        taxSchemeCountryCode: partyDraft.taxSchemeCountryCode,
      });
      if (Object.keys(patch).length > 0) {
        setPartyDraft((prev) => ({ ...prev, ...patch }));
      }
    }
    // Detect a manual override of the address name and stop mirroring the
    // party name into it from now on. We compare against the previous draft
    // value rather than partyDraft.name so we don't false-trigger when the
    // mirror itself flows through here.
    if (isNew && !primaryAddressNameDirty && next.name !== primaryAddressDraft.name) {
      setPrimaryAddressNameDirty(true);
    }
    setPrimaryAddressDraft(next);
  };

  // Companion handler for the billing address — same dirty-flag rule so
  // mirroring stops as soon as the operator types into the billing name.
  const handleBillingAddressChange = (next: AddressDraft) => {
    if (isNew && !billingAddressNameDirty && next.name !== billingAddressDraft.name) {
      setBillingAddressNameDirty(true);
    }
    setBillingAddressDraft(next);
  };

  // Party-name onChange in create mode mirrors the value into both
  // address-name fields whose dirty flag is still false. Edit mode just
  // updates the party name — addresses stay independent.
  const handlePartyNameChange = (value: string) => {
    setPartyDraft((prev) => ({ ...prev, name: value }));
    if (!isNew) return;
    if (!primaryAddressNameDirty) {
      setPrimaryAddressDraft((prev) => ({ ...prev, name: value }));
    }
    if (!billingAddressNameDirty) {
      setBillingAddressDraft((prev) => ({ ...prev, name: value }));
    }
  };

  // ── Render guards (edit mode only — create mode has nothing to load) ──
  if (!isNew) {
    if (partyQuery.isPending || primaryAddressQuery.isPending || billingAddressQuery.isPending) {
      return (
        <div className="flex h-full flex-col gap-4 px-2 py-4">
          <BackLink routeBase={routeBase} t={t} />
          <Loading label={t('parties:details.loading')} />
        </div>
      );
    }
    if (partyQuery.isError) {
      return (
        <div className="flex h-full flex-col gap-4 px-2 py-4">
          <BackLink routeBase={routeBase} t={t} />
          <ErrorBanner title={t('parties:details.loadFailed')} message={partyQuery.error.message} />
        </div>
      );
    }
    if (!seeded) {
      return (
        <div className="flex h-full flex-col gap-4 px-2 py-4">
          <BackLink routeBase={routeBase} t={t} />
          <Loading label={t('parties:details.loading')} />
        </div>
      );
    }
  }

  // ── Validation ────────────────────────────────────────────────────────
  // The validation here mirrors the BE FluentValidation rules. Address
  // requires Name + Address1 + 2-letter Country; Postcode is optional at
  // the schema level (BE allows null) but most country flows want it.
  // Geolocation is required for save — operators must verify the address
  // either via PlacesAutocomplete or the "Find on map" geocode button
  // before the green tick lights up. Mirrors the OrganisationPage's
  // main-office address gate so the bar is uniform across the suite.
  const addressIsValid = (a: AddressDraft) =>
    a.name.trim().length > 0 &&
    a.fullAddress.trim().length > 0 &&
    a.placeId.trim().length > 0 &&
    a.countryIsoCode.length === 2 &&
    a.latitude != null &&
    a.longitude != null;

  const isValid =
    partyDraft.code.trim().length > 0 &&
    partyDraft.name.trim().length > 0 &&
    partyDraft.billingName.trim().length > 0 &&
    partyDraft.taxSchemeCountryCode.length === 2 &&
    partyDraft.currencyIsoCode.length === 3 &&
    addressIsValid(primaryAddressDraft) &&
    (billingSameAsPrimary || addressIsValid(billingAddressDraft));

  // Build a list of human-readable reasons for why Save is disabled.
  // Same predicate set as `isValid` above, but each branch adds its own
  // i18n-resolved label so the hint near the Save button can tell the
  // operator exactly which control needs attention. Geolocation is
  // typically the non-obvious blocker — operators forget to verify the
  // address with Places autocomplete or the "Find on map" button.
  const missingFields: string[] = [];
  if (!partyDraft.code.trim()) missingFields.push(t('parties:fields.code'));
  if (!partyDraft.name.trim()) missingFields.push(t('parties:fields.name'));
  if (!partyDraft.billingName.trim()) missingFields.push(t('parties:fields.billingName'));
  if (partyDraft.taxSchemeCountryCode.length !== 2)
    missingFields.push(t('parties:fields.taxSchemeCountry'));
  if (partyDraft.currencyIsoCode.length !== 3) missingFields.push(t('parties:fields.currency'));
  if (!addressIsValid(primaryAddressDraft)) {
    // Distinguish "fields missing" from "geolocation missing" — the
    // latter is the most common gotcha so we call it out specifically.
    if (
      primaryAddressDraft.name.trim() &&
      primaryAddressDraft.fullAddress.trim() &&
      primaryAddressDraft.countryIsoCode.length === 2
    ) {
      missingFields.push(t('parties:details.primaryAddressLocation'));
    } else {
      missingFields.push(t('parties:details.primaryAddress'));
    }
  }
  if (!billingSameAsPrimary && !addressIsValid(billingAddressDraft)) {
    if (
      billingAddressDraft.name.trim() &&
      billingAddressDraft.fullAddress.trim() &&
      billingAddressDraft.countryIsoCode.length === 2
    ) {
      missingFields.push(t('parties:details.billingAddressLocation'));
    } else {
      missingFields.push(t('parties:details.billingAddress'));
    }
  }

  // ── Save handler ──────────────────────────────────────────────────────
  // Edit and create flows diverge on number of mutations and ordering:
  //   - Edit: 3 mutations in parallel (primary update, billing update,
  //     party update). One toast, one summary.
  //   - Create: 3 mutations in series — addresses first (we need their
  //     ids), then party. Navigate on success.
  // Errors at any stage abort the chain; the partial state is left as-is
  // (no rollback) — operator sees the toast and can retry.
  const handleSave = async () => {
    if (!orgId || !isValid) return;
    const trimmedTermDays = partyDraft.paymentTermDays.trim();
    const partyTermDays = trimmedTermDays === '' ? null : Number(trimmedTermDays);
    if (partyTermDays !== null && (!Number.isInteger(partyTermDays) || partyTermDays < 0)) {
      showToast({ message: t('parties:errors.paymentTermDays'), type: 'error' });
      return;
    }

    // Blank = inherit the organisation default. Merged into the stored map
    // rather than replacing it, so another app's choices survive a save from
    // here. A supplier-only party keeps whatever is stored untouched: it is
    // neither invoiced nor credited, so this form never showed the fields and
    // must not silently clear them.
    const preferredDocumentLayoutsJson = billsAsCustomer
      ? writeLayoutPrefs(partyDraft.storedLayoutPrefsJson, {
          Invoice: partyDraft.preferredInvoiceTemplateId || null,
          CreditNote: partyDraft.preferredCreditNoteTemplateId || null,
        })
      : partyDraft.storedLayoutPrefsJson;

    const creditLimitNum = Number(partyDraft.creditLimit);
    if (Number.isNaN(creditLimitNum) || creditLimitNum < 0) {
      showToast({ type: 'error', message: t('parties:details.invalidCreditLimit') });
      return;
    }

    // Required order references travel on their OWN endpoint — the party PUT is
    // a full replace and the Depot party screen, which knows nothing about this
    // TMS-only rule, would blank it on its next save. Written only when it
    // actually changed, compared against the stored column: both sides are
    // normalised (distinct, ascending, empty as null), so this is a plain string
    // comparison rather than a set difference.
    //
    // A supplier-only party never shows the control, so it sends nothing — the
    // BE would refuse the write anyway, and a toast about it would be noise
    // about a field the operator never saw.
    const requiredReferencesJson = billsAsCustomer
      ? serialiseRequiredOrderReferenceSlots(partyDraft.requiredOrderReferenceSlots)
      : null;

    // Resolve effective billing draft — when "Same as primary" is on, we
    // copy the primary fields into billing so the BE record stays
    // independently editable later (see comment in handler doc).
    const effectiveBillingDraft: AddressDraft = billingSameAsPrimary
      ? { ...primaryAddressDraft }
      : billingAddressDraft;

    try {
      if (isNew) {
        // Sequential create — addresses first so we can pass the new ids
        // to createParty.
        const primaryAddressId = await createAddressMutation.mutateAsync({
          organisationId: orgId,
          addressType: 'Primary',
          partyId: null,
          ...toCreateAddressBody(primaryAddressDraft),
        });
        const billingAddressId = billingSameAsPrimary
          ? primaryAddressId
          : await createAddressMutation.mutateAsync({
              organisationId: orgId,
              addressType: 'Billing',
              partyId: null,
              ...toCreateAddressBody(effectiveBillingDraft),
            });

        const body: CreatePartyRequest = {
          organisationId: orgId,
          code: partyDraft.code.trim(),
          name: partyDraft.name.trim(),
          type: mode,
          // Subtype is supplier-only — Customer parties always send
          // null (BE expects null on Customer rows). Supplier mode
          // sends whatever the operator picked (defaults to Generic).
          subtype: mode === 'Supplier' ? partyDraft.subtype : null,
          status: partyDraft.status,
          primaryAddressId,
          billingAddressId,
          billingName: partyDraft.billingName.trim(),
          billingFrequency: partyDraft.billingFrequency,
          paymentMethod: partyDraft.paymentMethod,
          creditLimit: creditLimitNum,
          // Blank = no customer-specific terms; null tells the BE to inherit.
          paymentTermDays: partyTermDays,
          paymentTermBasis: partyTermDays === null ? null : partyDraft.paymentTermBasis,
          taxNumber: partyDraft.taxNumber.trim() || null,
          taxSchemeCountryCode: partyDraft.taxSchemeCountryCode.toUpperCase(),
          euVatStatement: partyDraft.euVatStatement,
          currencyIsoCode: partyDraft.currencyIsoCode.toUpperCase(),
          accountOwnerId: partyDraft.accountOwnerId || null,
          accountSystemName: partyDraft.accountSystemName.trim() || null,
          accountSystemCode: partyDraft.accountSystemCode.trim() || null,
          extraFields:
            partyDraft.extraFields.length > 0 ? JSON.stringify(partyDraft.extraFields) : null,
          tags: partyDraft.tags.trim() || null,
        };
        const newPartyId = await createPartyMutation.mutateAsync(body);
        // Create cannot carry it (the field has no home on CreateParty, by
        // design — one write path for the column), so a new customer that picked
        // references gets a second call. Skipped when nothing was picked, which
        // is the common case and already what an absent column means.
        if (requiredReferencesJson !== null) {
          await setRequiredReferencesMutation.mutateAsync({
            partyId: newPartyId,
            organisationId: orgId,
            slots: partyDraft.requiredOrderReferenceSlots,
          });
        }
        if (preferredDocumentLayoutsJson !== null) {
          await setDocumentLayoutsMutation.mutateAsync({
            partyId: newPartyId,
            organisationId: orgId,
            preferredDocumentLayoutsJson,
          });
        }
        showToast({ type: 'success', message: t('parties:details.created') });
        navigate(`${routeBase}/${newPartyId}`);
        return;
      }

      // Edit mode — three updates in parallel.
      const party = partyQuery.data!;
      const updatePartyBody: UpdatePartyRequest = {
        partyId: party.id,
        code: partyDraft.code.trim(),
        name: partyDraft.name.trim(),
        type: party.type, // type stays as-is (locked once persisted)
        // Subtype is editable on Supplier / Both parties. Customer
        // parties pass null. The draft always carries a concrete
        // SupplierTypeValue (defaults to Generic) so no empty-string
        // serialisation gymnastics needed.
        subtype: party.type === 'Supplier' || party.type === 'Both' ? partyDraft.subtype : null,
        status: partyDraft.status,
        primaryAddressId: party.primaryAddressId,
        billingAddressId: billingSameAsPrimary ? party.primaryAddressId : party.billingAddressId,
        billingName: partyDraft.billingName.trim(),
        billingFrequency: partyDraft.billingFrequency,
        paymentMethod: partyDraft.paymentMethod,
        creditLimit: creditLimitNum,
        // Blank = no customer-specific terms; null tells the BE to inherit.
        paymentTermDays: partyTermDays,
        paymentTermBasis: partyTermDays === null ? null : partyDraft.paymentTermBasis,
        taxNumber: partyDraft.taxNumber.trim() || null,
        taxSchemeCountryCode: partyDraft.taxSchemeCountryCode.toUpperCase(),
        euVatStatement: partyDraft.euVatStatement,
        currencyIsoCode: partyDraft.currencyIsoCode.toUpperCase(),
        accountOwnerId: partyDraft.accountOwnerId || null,
        accountSystemName: partyDraft.accountSystemName.trim() || null,
        accountSystemCode: partyDraft.accountSystemCode.trim() || null,
        extraFields:
          partyDraft.extraFields.length > 0 ? JSON.stringify(partyDraft.extraFields) : null,
        tags: partyDraft.tags.trim() || null,
      };
      const primaryUpdateBody: UpdateAddressRequest = {
        addressId: party.primaryAddressId,
        addressType: 'Primary',
        ...toUpdateAddressBody(primaryAddressDraft),
        status: primaryAddressQuery.data?.status ?? 'Active',
        // Echoed, not edited here: the address PUT is a full replace and this
        // form has no tag control, but the same row is taggable on the Locations
        // screen. Omitted, saving a party would clear its address's tags.
        serviceTags: primaryAddressQuery.data?.serviceTags ?? null,
      };
      // When "Same as primary" is on, `party.billingAddressId` and
      // `party.primaryAddressId` point at the same record, so issuing
      // both updates in parallel was racing — both mutations targeted
      // the same row, the BE processed them in arrival order, and the
      // last writer's `addressType` won. Result: the record's type
      // flipped between Primary and Billing on every save. Detect the
      // shared-id case and fire only the Primary update.
      const billingMatchesPrimary = party.billingAddressId === party.primaryAddressId;
      const billingUpdateBody: UpdateAddressRequest | null = billingMatchesPrimary
        ? null
        : {
            addressId: party.billingAddressId,
            addressType: 'Billing',
            ...toUpdateAddressBody(effectiveBillingDraft),
            status: billingAddressQuery.data?.status ?? 'Active',
            serviceTags: billingAddressQuery.data?.serviceTags ?? null,
          };
      await Promise.all([
        updateAddressMutation.mutateAsync(primaryUpdateBody),
        ...(billingUpdateBody ? [updateAddressMutation.mutateAsync(billingUpdateBody)] : []),
        updatePartyMutation.mutateAsync({
          body: updatePartyBody,
          organisationId: orgId,
        }),
      ]);

      // After the replace, not alongside it: both writes land on the same row,
      // and sequencing them keeps the audit stamp and any future concurrency
      // token from being decided by arrival order.
      if (
        billsAsCustomer &&
        requiredReferencesJson !== (party.tmsRequiredOrderReferencesJson ?? null)
      ) {
        await setRequiredReferencesMutation.mutateAsync({
          partyId: party.id,
          organisationId: orgId,
          slots: partyDraft.requiredOrderReferenceSlots,
        });
      }

      // Layouts travel on their own endpoint for the same reason as the
      // references: the column is keyed by app, so it must never ride on a PUT
      // another app also makes. Written only when this screen changed it, so a
      // save from here cannot re-assert a stale read of another app's branch.
      if (
        billsAsCustomer &&
        preferredDocumentLayoutsJson !== (party.preferredDocumentLayoutsJson ?? null)
      ) {
        await setDocumentLayoutsMutation.mutateAsync({
          partyId: party.id,
          organisationId: orgId,
          preferredDocumentLayoutsJson,
        });
      }

      showToast({ type: 'success', message: t('parties:details.saved') });
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('parties:details.saveFailed'),
      });
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  const titleText = isNew
    ? isCustomer
      ? t('parties:details.newCustomer')
      : t('parties:details.newSupplier')
    : (partyQuery.data?.name ?? '');
  //const partyType = partyQuery.data?.type ?? mode;

  return (
    <div className="flex h-full flex-col px-2">
      <PageHeader
        title={titleText}
        description={t(
          isCustomer
            ? 'parties:details.descriptionCustomer'
            : 'parties:details.descriptionSupplier',
        )}
      />

      <FormSection title={t('parties:details.basicInfo')} columns={3} disabled={isBusy}>
        {/* ── Row 0 — supplier-only sub-classification ──────────────
            Spans the full 3-col width so it visibly sits ABOVE the
            three columns below. Inner sub-grid (same 3-col shape +
            gap-3 as the parent FormSection) keeps the picker itself
            at one-third width, aligned with the Code / Name fields
            in column 1 directly below. The other two sub-grid cells
            stay empty — the picker is the only thing on this row by
            design. Hidden entirely on Customer parties. */}
        {!isCustomer && (
          <div className="grid gap-3 md:col-span-3 md:grid-cols-3">
            <SelectInput
              label={t('parties:fields.subtype')}
              name="subtype"
              value={partyDraft.subtype}
              options={subtypeOptions}
              onChange={(e) =>
                setPartyDraft({
                  ...partyDraft,
                  subtype: e.target.value as SupplierTypeValue,
                })
              }
              disabled={isBusy}
            />
          </div>
        )}

        {/* ── Column 1 — identity, primary address, locality ────────── */}
        <div className="space-y-3">
          <TextInput
            label={requiredLabel(t('parties:fields.code'))}
            name="code"
            value={partyDraft.code}
            maxLength={64}
            onChange={(e) => setPartyDraft({ ...partyDraft, code: e.target.value })}
            disabled={isBusy}
          />
          <TextInput
            label={requiredLabel(t('parties:fields.name'))}
            name="name"
            value={partyDraft.name}
            maxLength={255}
            onChange={(e) => handlePartyNameChange(e.target.value)}
            disabled={isBusy}
          />
          <AddressInlineEditor
            label={t('parties:details.primaryAddress')}
            value={primaryAddressDraft}
            onChange={handlePrimaryAddressChange}
            countryOptions={COUNTRY_OPTIONS}
            hasCodeField
            hasNameField
            enablePlacesSearch
            showMap
          />
          <SelectInput
            label={requiredLabel(t('parties:fields.currency'))}
            name="currencyIsoCode"
            value={partyDraft.currencyIsoCode}
            options={CURRENCY_OPTIONS}
            onChange={(e) => setPartyDraft({ ...partyDraft, currencyIsoCode: e.target.value })}
            disabled={isBusy}
          />
          <TextInput
            label={t('parties:fields.taxNumber')}
            name="taxNumber"
            value={partyDraft.taxNumber}
            maxLength={64}
            onChange={(e) => setPartyDraft({ ...partyDraft, taxNumber: e.target.value })}
            disabled={isBusy}
          />
          {/* Network ID is read-only — driven by the (deferred) invitations
              subsystem. Set by the BE when this party gets linked to
              another organisation; not operator-editable. Same disabled-
              TextInput treatment as Link Status in column 3 so the
              read-only fields share a visual language. */}
          <TextInput
            label={t('parties:fields.networkId')}
            name="networkId"
            value={partyDraft.networkId}
            readOnly
            disabled
            onChange={() => {
              /* read-only — no-op */
            }}
          />
          <FieldGroup label={t('parties:fields.status')} inline>
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setPartyDraft({ ...partyDraft, status: s })}
                disabled={isBusy}
                className={chipClass(partyDraft.status === s)}
              >
                {statusLabels[s]}
              </button>
            ))}
          </FieldGroup>
          <ServiceTagsRow
            tags={applicableTags}
            tagSet={tagSet}
            isBusy={isBusy}
            onToggle={toggleTag}
            inline
          />
        </div>

        {/* ── Column 2 — billing identity, billing address, tax/VAT ── */}
        <div className="space-y-3">
          <TextInput
            label={requiredLabel(t('parties:fields.billingName'))}
            name="billingName"
            value={partyDraft.billingName}
            maxLength={255}
            onChange={(e) => setPartyDraft({ ...partyDraft, billingName: e.target.value })}
            disabled={isBusy}
          />
          <Switch
            label={
              t('parties:details.billingAddress') + ' (' + t('parties:details.sameAsPrimary') + ')'
            }
            name="billingSameAsPrimary"
            checked={billingSameAsPrimary}
            enabledLabel={t('common:states.yes')}
            disabledLabel={t('common:states.no')}
            disabled={isBusy}
            onChange={(e) => {
              const next = e.currentTarget.checked;
              setBillingSameAsPrimary(next);
              if (!next) {
                setBillingAddressDraft({ ...primaryAddressDraft });
              }
            }}
          />
          {!billingSameAsPrimary && (
            <AddressInlineEditor
              label={t('parties:details.billingAddress')}
              value={billingAddressDraft}
              onChange={handleBillingAddressChange}
              countryOptions={COUNTRY_OPTIONS}
              hasCodeField
              hasNameField
              enablePlacesSearch
              showMap
            />
          )}
          <FieldGroup label={t('parties:fields.billingFrequency')} inline>
            <div className="flex flex-wrap gap-1.5">
              {BILLING_FREQUENCY_OPTIONS.map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setPartyDraft({ ...partyDraft, billingFrequency: f })}
                  disabled={isBusy}
                  className={chipClass(partyDraft.billingFrequency === f)}
                >
                  {billingFrequencyLabels[f]}
                </button>
              ))}
            </div>
          </FieldGroup>
          <FieldGroup label={t('parties:fields.paymentMethod')} inline>
            <div className="flex flex-wrap gap-1.5">
              {PAYMENT_METHOD_OPTIONS.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setPartyDraft({ ...partyDraft, paymentMethod: m })}
                  disabled={isBusy}
                  className={chipClass(partyDraft.paymentMethod === m)}
                >
                  {paymentMethodLabels[m]}
                </button>
              ))}
            </div>
          </FieldGroup>
          {partyDraft.paymentMethod === 'Credit' && (
            <TextInput
              label={t('parties:fields.creditLimit')}
              name="creditLimit"
              type="number"
              value={partyDraft.creditLimit}
              onChange={(e) => setPartyDraft({ ...partyDraft, creditLimit: e.target.value })}
              disabled={isBusy}
            />
          )}
          {/* Payment terms. Blank days = follow the organisation's house terms,
              which is the common case — so the inherited wording is shown
              rather than silently pre-filling a copy that would then stop
              tracking the organisation.

              Only shown where terms mean something: a credit account is
              invoiced on them, and a self-billing account still pays on a cycle
              we need for aged debt even though we issue no document. Prepay is
              settled on despatch and an internal account is never invoiced, so
              for those two there is no period to state.

              Hiding does NOT clear the stored value - the same rule as the
              layout preferences on supplier parties. Switching a customer to
              prepay and back should not quietly discard terms somebody
              negotiated, and while hidden the value drives nothing.

              Days and basis are one setting, so they sit on one row and share a
              single hint line beneath. The basis is disabled while days is
              blank: with no days of its own the customer follows the
              organisation, and counting from a date it does not have would be
              a choice with nothing to apply it to. */}
          {showsPaymentTerms && (
            <div className="flex flex-col gap-1">
              <div className="grid grid-cols-2 gap-2">
                <TextInput
                  label={
                    t('parties:fields.paymentTerms') + ' ' + t('parties:fields.paymentTermDays')
                  }
                  name="paymentTermDays"
                  type="number"
                  min={0}
                  // The inherited figure as a placeholder, so an empty box reads
                  // as "following the organisation" rather than as "unset".
                  placeholder={String(inheritedTerms.days)}
                  value={partyDraft.paymentTermDays}
                  onChange={(e) =>
                    setPartyDraft({ ...partyDraft, paymentTermDays: e.target.value })
                  }
                  disabled={isBusy}
                />
                <SelectInput
                  label={t('parties:fields.paymentTermBasis')}
                  name="paymentTermBasis"
                  value={partyDraft.paymentTermBasis}
                  options={[
                    { value: 'InvoiceDate', label: PAYMENT_TERM_BASIS_LABEL.InvoiceDate },
                    { value: 'EndOfMonth', label: PAYMENT_TERM_BASIS_LABEL.EndOfMonth },
                  ]}
                  disabled={isBusy || partyDraft.paymentTermDays.trim() === ''}
                  onChange={(e) =>
                    setPartyDraft({
                      ...partyDraft,
                      paymentTermBasis: e.target.value as PaymentTermBasisValue,
                    })
                  }
                />
              </div>
              <p className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                {partyDraft.paymentTermDays.trim() === ''
                  ? t('parties:fields.paymentTermsInherited', {
                      terms: describePaymentTerms(inheritedTerms),
                    })
                  : t('parties:fields.paymentTermsOwn', {
                      terms: describePaymentTerms({
                        days: Number(partyDraft.paymentTermDays) || 0,
                        basis: partyDraft.paymentTermBasis,
                      }),
                    })}
                {partyDraft.paymentTermDays.trim() !== '' && (
                  <button
                    type="button"
                    disabled={isBusy}
                    onClick={() => setPartyDraft({ ...partyDraft, paymentTermDays: '' })}
                    className="text-sky-700 hover:underline"
                  >
                    {t('parties:fields.useOrganisationTerms')}
                  </button>
                )}
              </p>
            </div>
          )}

          <SelectInput
            label={requiredLabel(t('parties:fields.taxSchemeCountry'))}
            name="taxSchemeCountryCode"
            value={partyDraft.taxSchemeCountryCode}
            options={COUNTRY_OPTIONS}
            onChange={(e) => setPartyDraft({ ...partyDraft, taxSchemeCountryCode: e.target.value })}
            disabled={isBusy}
          />
          <Switch
            label={t('parties:fields.euVatStatement')}
            name="euVatStatement"
            checked={partyDraft.euVatStatement}
            enabledLabel={t('common:states.yes')}
            disabledLabel={t('common:states.no')}
            disabled={isBusy}
            onChange={(e) =>
              setPartyDraft({
                ...partyDraft,
                euVatStatement: e.currentTarget.checked,
              })
            }
          />
        </div>

        {/* ── Column 3 — link / accounting system ─── */}
        <div className="space-y-3">
          <TextInput
            label={t('parties:fields.linkStatus')}
            name="linkStatus"
            value={invitationStatusLabels[partyQuery.data?.invitationStatus ?? 'NotInvited']}
            readOnly
            disabled
            onChange={() => {}}
          />
          <SelectInput
            label={t('parties:fields.accountOwner')}
            name="accountOwnerId"
            value={partyDraft.accountOwnerId}
            options={accountOwnerOptions}
            onChange={(e) => setPartyDraft({ ...partyDraft, accountOwnerId: e.target.value })}
            disabled={isBusy}
          />
          <TextInput
            label={t('parties:fields.accountSystemName')}
            name="accountSystemName"
            value={partyDraft.accountSystemName}
            maxLength={64}
            onChange={(e) => setPartyDraft({ ...partyDraft, accountSystemName: e.target.value })}
            disabled={isBusy}
          />
          <TextInput
            label={t('parties:fields.accountSystemCode')}
            name="accountSystemCode"
            value={partyDraft.accountSystemCode}
            maxLength={64}
            onChange={(e) => setPartyDraft({ ...partyDraft, accountSystemCode: e.target.value })}
            disabled={isBusy}
          />
          {/* Document layouts. Customers only — a supplier is neither invoiced
              nor credited, so the choice would be a setting nothing reads. */}
          {billsAsCustomer && (
            <>
              <LayoutPicker
                label={t('parties:fields.invoiceLayout')}
                name="preferredInvoiceTemplateId"
                layouts={invoiceLayouts}
                layoutsLoaded={layoutsLoaded}
                value={partyDraft.preferredInvoiceTemplateId}
                disabled={isBusy}
                t={t}
                onChange={(next) =>
                  setPartyDraft({ ...partyDraft, preferredInvoiceTemplateId: next })
                }
              />
              <LayoutPicker
                label={t('parties:fields.creditNoteLayout')}
                name="preferredCreditNoteTemplateId"
                layouts={creditNoteLayouts}
                layoutsLoaded={layoutsLoaded}
                value={partyDraft.preferredCreditNoteTemplateId}
                disabled={isBusy}
                t={t}
                onChange={(next) =>
                  setPartyDraft({ ...partyDraft, preferredCreditNoteTemplateId: next })
                }
              />
              {/* Required references. Sits with the layouts because it is the
                  same kind of setting — this customer's override of something
                  the organisation configured once for everyone. Customers only:
                  orders are raised FOR a customer, so only a customer can demand
                  a reference on one (the BE refuses it on a supplier). */}
              <FieldGroup label={t('parties:fields.requiredOrderReferences')}>
                {configuredReferenceSlots.length === 0 ? (
                  <p className="text-xs text-slate-500">
                    {t('parties:fields.requiredOrderReferencesNoSlots')}
                  </p>
                ) : (
                  <>
                    <div className="flex flex-wrap gap-1.5">
                      {configuredReferenceSlots.map((slot) => {
                        const picked = partyDraft.requiredOrderReferenceSlots.includes(slot);
                        return (
                          <button
                            key={slot}
                            type="button"
                            disabled={isBusy}
                            aria-pressed={picked}
                            onClick={() =>
                              setPartyDraft({
                                ...partyDraft,
                                // Kept ascending so the draft matches what the
                                // BE stores, and "has this changed?" on save
                                // stays a string comparison.
                                requiredOrderReferenceSlots: picked
                                  ? partyDraft.requiredOrderReferenceSlots.filter((s) => s !== slot)
                                  : [...partyDraft.requiredOrderReferenceSlots, slot].sort(
                                      (a, b) => a - b,
                                    ),
                              })
                            }
                            className={chipClass(picked)}
                          >
                            {resolveOrderReferenceLabel(orderReferenceLabels, slot - 1)}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </FieldGroup>
            </>
          )}
          <ExtraFieldsList
            fields={partyDraft.extraFields}
            onChange={(next) => setPartyDraft({ ...partyDraft, extraFields: next })}
          />
        </div>

        {/* ── Bottom row 2 — Action buttons spanning all 3 cols ──────
            When Save is disabled because of validation, render a small
            amber hint to the left of the buttons listing exactly which
            controls are blocking — geolocation in particular is easy to
            miss when the address summary is collapsed and looks fine at
            a glance. */}
        <div className="flex items-center justify-between gap-2 pt-0 md:col-span-3">
          <div className="flex items-center gap-2">
            {!isNew && (
              <button
                type="button"
                aria-label={t('common:actions.delete')}
                onClick={() => setPendingDelete(true)}
                disabled={isBusy}
                className="flex h-7 w-7 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
            {/* Beside delete rather than in a section of its own: both act on
                the record as a whole, and the history is something you open
                when you have a question, not something to scroll past. */}
            {!isNew && partyQuery.data && (
              <PartyHistoryButton
                partyId={partyQuery.data.id}
                organisationId={orgId}
                partyName={partyQuery.data.name}
              />
            )}
            {!isValid && missingFields.length > 0 && (
              <p className="text-[11px] italic text-amber-700" role="status" aria-live="polite">
                {t('parties:details.saveDisabledHint', {
                  fields: missingFields.join(', '),
                })}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label={t('common:actions.cancel')}
              onClick={() => navigate(routeBase)}
              disabled={isBusy}
              className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200 disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              aria-label={t('common:actions.save')}
              onClick={handleSave}
              disabled={isBusy || !isValid}
              className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
            >
              <Check className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </FormSection>

      {/* Sub-sections — Contacts / Addresses / Documents in a 3-column
          grid at the bottom of the page, mirroring the legacy layout.
          Each section is gated on `!isNew` (the sub-aggregates need a
          party id to bind to); in create mode every column shows the
          same "save first" placeholder so the layout stays balanced. */}
      <div className="grid grid-cols-1 gap-3 pt-3 md:grid-cols-3">
        {!isNew && partyQuery.data ? (
          <PartyContactsSection partyId={partyQuery.data.id} />
        ) : (
          <SubSectionPlaceholder
            title={t('parties:contacts.title')}
            message={t('parties:details.saveFirstToManage')}
          />
        )}
        {!isNew && partyQuery.data && orgId ? (
          <PartyAddressesSection
            organisationId={orgId}
            partyId={partyQuery.data.id}
            primaryAddressId={partyQuery.data.primaryAddressId}
            billingAddressId={partyQuery.data.billingAddressId}
          />
        ) : (
          <SubSectionPlaceholder
            title={t('parties:additionalAddresses.title')}
            message={t('parties:details.saveFirstToManage')}
          />
        )}
        {!isNew && partyQuery.data ? (
          <PartyDocumentsSection partyId={partyQuery.data.id} />
        ) : (
          <SubSectionPlaceholder
            title={t('parties:documents.title')}
            message={t('parties:details.saveFirstToManage')}
          />
        )}
      </div>

      {/* Rates & Charges — full-width, below the 3-column grid. Live per-party
          rate card (Transport / Fuel / Additionals) read + written against the
          customer-rates endpoints, keyed by this party's id.
          Gated on a saved party + org id, like the sub-sections above. */}
      {!isNew && partyQuery.data && orgId && (
        <div className="pt-3">
          <PartyRatesAndChargesSection
            organisationId={orgId}
            customerPartyId={partyQuery.data.id}
            currencyIsoCode={partyDraft.currencyIsoCode}
            billingFrequency={partyDraft.billingFrequency}
            paymentMethod={partyDraft.paymentMethod}
            mode={mode}
          />
        </div>
      )}

      <ConfirmDialog
        open={pendingDelete}
        tone="danger"
        title={t(isCustomer ? 'parties:customers.delete.title' : 'parties:suppliers.delete.title')}
        message={
          partyQuery.data
            ? t(
                isCustomer
                  ? 'parties:customers.delete.message'
                  : 'parties:suppliers.delete.message',
                { name: partyQuery.data.name },
              )
            : null
        }
        confirmLabel={t('common:actions.delete')}
        onConfirm={async () => {
          if (!partyQuery.data || !orgId) return;
          try {
            await deletePartyMutation.mutateAsync({
              id: partyQuery.data.id,
              organisationId: orgId,
            });
            showToast({ type: 'success', message: t('parties:details.deleted') });
            navigate(routeBase);
          } catch (err) {
            showToast({
              type: 'error',
              message: err instanceof Error ? err.message : t('parties:details.deleteFailed'),
            });
          } finally {
            setPendingDelete(false);
          }
        }}
        onCancel={() => setPendingDelete(false)}
        busy={deletePartyMutation.isPending}
      />
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

/**
 * Picks which layout a customer's documents print with.
 *
 * Two things this has to make obvious, because they look the same from the
 * outside and behave differently:
 *
 *  - **Unset follows the organisation.** It is the normal case, so it is the
 *    first option and it NAMES the layout it currently resolves to rather than
 *    saying "default" and leaving the operator to go and look.
 *  - **Naming that same layout explicitly pins it.** If the organisation's
 *    default later changes, an inheriting customer moves with it and a pinned
 *    one does not. The default layout is therefore still listed, tagged as the
 *    current default so the pair does not read as a duplicate.
 *
 * A stored key with no matching layout is reported rather than silently reading
 * as "default" — the operator chose something and it is gone.
 */
function LayoutPicker({
  label,
  name,
  layouts,
  layoutsLoaded,
  value,
  disabled,
  onChange,
  t,
}: {
  label: string;
  name: string;
  layouts: DocumentTemplate[];
  /** False while the list is still loading — nothing can be judged missing yet. */
  layoutsLoaded: boolean;
  value: string;
  disabled: boolean;
  onChange: (next: string) => void;
  t: ReturnType<typeof useTranslation<['parties', 'common']>>['t'];
}) {
  const defaultLayout = layouts.find((tpl) => tpl.isDefault) ?? null;
  const missing = value !== '' && layoutsLoaded && !layouts.some((tpl) => tpl.id === value);

  // The first option is "follow the organisation", carrying the empty value.
  // The default layout is ALSO listed and tagged, because the two differ: the
  // first follows the organisation if its default later changes, the second
  // pins this layout regardless.
  const options: SelectInputOption[] = [
    {
      value: '',
      label: defaultLayout
        ? t('parties:fields.layoutInherited', { layout: defaultLayout.name })
        : t('parties:fields.layoutInheritedUnknown'),
    },
    ...layouts.map((tpl) => ({
      value: tpl.id,
      label: tpl.isDefault ? t('parties:fields.layoutIsDefault', { layout: tpl.name }) : tpl.name,
    })),
  ];

  return (
    <SelectInput
      label={label}
      name={name}
      value={missing ? '' : value}
      options={options}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      // helperText={missing ? t('parties:fields.layoutMissing') : t('parties:fields.layoutHint')}
    />
  );
}

function BackLink({
  routeBase,
  t,
}: {
  routeBase: string;
  t: ReturnType<typeof useTranslation<['parties', 'common']>>['t'];
}) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate(routeBase)}
      className="inline-flex w-fit items-center gap-1 text-xs text-slate-500 hover:text-slate-800"
    >
      <ArrowLeft className="h-3.5 w-3.5" />
      {t('parties:details.backToList')}
    </button>
  );
}

/**
 * Stand-in card for sub-sections that aren't yet built (Addresses,
 * Documents) or that can't bind in create mode (Contacts before save).
 * Same outer chrome as `PartyContactsSection` so the 3-column grid
 * stays visually balanced regardless of which sections are real.
 */
function SubSectionPlaceholder({ title, message }: { title: string; message: string }) {
  return (
    <section className="rounded-sm border border-slate-300 bg-white p-3 shadow-sm">
      <header className="mb-2">
        <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
      </header>
      <p className="px-3 py-4 text-center text-xs italic text-slate-400">{message}</p>
    </section>
  );
}

function ServiceTagsRow({
  tags,
  tagSet,
  isBusy,
  onToggle,
  inline = false,
}: {
  tags: ServiceTag[];
  tagSet: Set<string>;
  isBusy: boolean;
  onToggle: (tag: ServiceTag) => void;
  /**
   * When true, render label + chips on a single line ("Service Tags: [chip] [chip]…")
   * to match the inline `<FieldGroup>` convention used at the bottom of
   * the Information card.
   */
  inline?: boolean;
}) {
  const { t } = useTranslation('parties');
  const chips =
    tags.length === 0 ? (
      <p className="text-[10px] italic text-slate-400">{t('details.noServiceTagsConfigured')}</p>
    ) : (
      tags.map((tag) => {
        const isActive = tagSet.has(tag.name.toLowerCase());
        return (
          <button
            key={tag.id}
            type="button"
            onClick={() => onToggle(tag)}
            disabled={isBusy}
            title={tag.description ?? undefined}
            className={tagChipClass(isActive)}
          >
            {tag.name}
          </button>
        );
      })
    );

  if (inline) {
    // Same `min-h-9` rationale as inline FieldGroup — keeps Service Tags
    // flush with TextInput rows when sitting next to them in a column.
    return (
      <div className="flex min-h-9 flex-wrap items-center gap-2">
        <p className="text-[11px] font-medium text-slate-600">{t('details.serviceTags')}:</p>
        {chips}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-slate-600">{t('details.serviceTags')}</p>
      {tags.length === 0 ? (
        <p className="text-[10px] italic text-slate-400">{t('details.noServiceTagsConfigured')}</p>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5">{chips}</div>
      )}
    </div>
  );
}

/**
 * Label + inputs grouping. Two layouts:
 *
 *  - **Stacked (default):** label on top, children below — matches the
 *    floating-label pattern of TextInput / SelectInput.
 *  - **Inline (`inline`):** label, a colon, then the children on the
 *    same row. Used at the bottom of the Information card so Status /
 *    Service Tags read like compact metadata rather than full form
 *    fields.
 */
function FieldGroup({
  label,
  children,
  inline = false,
}: {
  label: string;
  children: React.ReactNode;
  inline?: boolean;
}) {
  if (inline) {
    // `min-h-9` matches TextInput/SelectInput's `h-9` so an inline
    // FieldGroup sitting next to (or alternating with) text fields in
    // the same column lines up vertically — without it the chip-only
    // row collapses to ~22px and the column flow looks ragged.
    return (
      <div className="flex min-h-9 flex-wrap items-center gap-2">
        <p className="text-[11px] font-medium text-slate-600">{label}:</p>
        {children}
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <p className="text-[11px] font-medium text-slate-600">{label}</p>
      {children}
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Address-draft → CreateAddressRequest body slice. `organisationId`,
 * `addressType`, and `partyId` are filled in by the caller because they
 * come from outer context (mode, owning party id) rather than the field
 * draft itself.
 */
function toCreateAddressBody(
  draft: AddressDraft,
): Omit<CreateAddressRequest, 'organisationId' | 'addressType' | 'partyId' | 'siteId'> {
  return {
    code: draft.code.trim() || null,
    name: draft.name.trim(),
    fullAddress: draft.fullAddress.trim(),
    city: draft.city.trim() || null,
    postcode: draft.postcode.trim() || null,
    countryIsoCode: draft.countryIsoCode.toUpperCase(),
    latitude: draft.latitude,
    longitude: draft.longitude,
    coordinatesSource: draft.coordinatesSource ?? null,
    placeId: draft.placeId.trim(),
  };
}

/**
 * Address-draft → UpdateAddressRequest body slice. Same trim/null-empty
 * rules as create. `addressId`, `addressType`, and `status` are filled
 * in by the caller (the BE requires status on every PUT — we echo the
 * loaded status verbatim since the basic-info form doesn't expose it).
 */
function toUpdateAddressBody(
  draft: AddressDraft,
): Omit<UpdateAddressRequest, 'addressId' | 'addressType' | 'status' | 'serviceTags'> {
  return {
    code: draft.code.trim() || null,
    name: draft.name.trim(),
    fullAddress: draft.fullAddress.trim(),
    city: draft.city.trim() || null,
    postcode: draft.postcode.trim() || null,
    countryIsoCode: draft.countryIsoCode.toUpperCase(),
    latitude: draft.latitude,
    longitude: draft.longitude,
    coordinatesSource: draft.coordinatesSource ?? null,
    placeId: draft.placeId.trim(),
  };
}

function requiredLabel(text: string) {
  return (
    <>
      {text} <span className="text-red-500">*</span>
    </>
  );
}

function chipClass(active: boolean): string {
  const base =
    'inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors disabled:opacity-50';
  return `${base} ${
    active
      ? 'border-sky-700 bg-sky-700 text-white'
      : 'border-slate-300 bg-white text-slate-600 hover:border-slate-500 hover:text-slate-800'
  }`;
}

function tagChipClass(active: boolean): string {
  const base =
    'inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors disabled:opacity-50';
  return `${base} ${
    active
      ? 'border-amber-600 bg-amber-600 text-white'
      : 'border-amber-300 bg-amber-50 text-amber-900 hover:border-amber-500 hover:text-amber-950'
  }`;
}
